import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, Eye, EyeOff, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../api";
import { Card, PageHeader, EmptyState, formatDateTime } from "../ui";

const CATEGORIES = ["Notícia", "Artigo", "Manifesto", "Posicionamento"];

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const blankForm = () => ({
  title: "",
  summary: "",
  content: "",
  category: "Notícia",
  author: "",
  source_url: "",
  published: true,
});

export default function AdminNoticias() {
  const [items, setItems] = useState(null);
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState(blankForm());
  const [coverFile, setCoverFile] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editCover, setEditCover] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    api
      .get("/admin/news")
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Não foi possível carregar as publicações."));

  useEffect(() => {
    load();
  }, []);

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!form.title || form.title.trim().length < 3) {
      toast.error("Informe um título válido.");
      return;
    }
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => {
      if (typeof v === "boolean") fd.append(k, v ? "true" : "false");
      else fd.append(k, v || "");
    });
    if (coverFile) fd.append("cover", coverFile);
    setBusy(true);
    try {
      await api.post("/admin/news", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Publicação criada!");
      setOpenCreate(false);
      setForm(blankForm());
      setCoverFile(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (n) => {
    setEditing(n);
    setEditForm({
      title: n.title || "",
      summary: n.summary || "",
      content: n.content || "",
      category: n.category || "Notícia",
      author: n.author || "",
      source_url: n.source_url || "",
      published: !!n.published,
    });
    setEditCover(null);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editing) return;
    const fd = new FormData();
    Object.entries(editForm).forEach(([k, v]) => {
      if (typeof v === "boolean") fd.append(k, v ? "true" : "false");
      else fd.append(k, v || "");
    });
    if (editCover) fd.append("cover", editCover);
    setBusy(true);
    try {
      await api.patch(`/admin/news/${editing.id}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Publicação atualizada!");
      setEditing(null);
      setEditForm(null);
      setEditCover(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const togglePublished = async (n) => {
    try {
      const fd = new FormData();
      fd.append("published", !n.published ? "true" : "false");
      await api.patch(`/admin/news/${n.id}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(
        !n.published ? "Publicação publicada." : "Publicação despublicada.",
      );
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (n) => {
    if (!window.confirm(`Excluir "${n.title}"? Esta ação não pode ser desfeita.`))
      return;
    try {
      await api.delete(`/admin/news/${n.id}`);
      toast.success("Publicação excluída.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-noticias">
      <PageHeader
        title="Notícias e Artigos"
        subtitle="Crie e gerencie posicionamentos, análises, notícias e artigos publicados pela ABLEMP. Conteúdos publicados aparecem em /noticias-e-artigos."
        actions={
          <button
            data-testid="new-news-btn"
            onClick={() => {
              setOpenCreate((v) => !v);
              setForm(blankForm());
              setCoverFile(null);
            }}
            className="inline-flex items-center gap-2 bg-[#0A0F14] text-white px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#002776]"
          >
            <Plus size={14} /> Nova publicação
          </button>
        }
      />

      {openCreate && (
        <Card className="p-5 mb-6">
          <NewsForm
            form={form}
            setForm={setForm}
            file={coverFile}
            setFile={setCoverFile}
            onSubmit={submitCreate}
            onCancel={() => setOpenCreate(false)}
            busy={busy}
            submitLabel="Publicar"
          />
        </Card>
      )}

      {items === null ? (
        <div className="text-sm text-slate-500">Carregando...</div>
      ) : items.length === 0 ? (
        <EmptyState
          title="Nenhuma publicação"
          hint="Crie a primeira notícia ou artigo usando o botão acima."
        />
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="text-left p-3">Título</th>
                  <th className="text-left p-3">Categoria</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Atualizado</th>
                  <th className="text-right p-3">Ações</th>
                </tr>
              </thead>
              <tbody>
                {items.map((n) => (
                  <tr
                    key={n.id}
                    data-testid={`news-row-${n.id}`}
                    className="border-t border-slate-100 hover:bg-slate-50"
                  >
                    <td className="p-3">
                      <div className="font-medium leading-snug">{n.title}</div>
                      {n.summary && (
                        <div className="mt-1 text-xs text-slate-500 line-clamp-2">
                          {n.summary}
                        </div>
                      )}
                    </td>
                    <td className="p-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-[#002776]/30 bg-[#002776]/8 text-[#002776] text-[11px]">
                        {n.category}
                      </span>
                    </td>
                    <td className="p-3">
                      {n.published ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 text-[11px]">
                          <Eye size={11} /> Publicado
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-slate-200 bg-slate-100 text-slate-600 text-[11px]">
                          <EyeOff size={11} /> Rascunho
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-slate-600">
                      {formatDateTime(n.updated_at || n.created_at)}
                    </td>
                    <td className="p-3 text-right">
                      <div className="inline-flex gap-1">
                        {n.published && (
                          <a
                            data-testid={`news-view-${n.id}`}
                            href={`/noticias-e-artigos/${n.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Abrir publicação"
                            className="inline-flex items-center gap-1 px-2 py-1 border border-slate-300 text-[10px] uppercase tracking-wide hover:border-[#002776]"
                          >
                            <ExternalLink size={12} />
                          </a>
                        )}
                        <button
                          data-testid={`news-edit-${n.id}`}
                          onClick={() => openEdit(n)}
                          title="Editar"
                          className="px-2 py-1 border border-slate-300 text-[10px] uppercase tracking-wide hover:border-[#002776]"
                        >
                          <Pencil size={12} />
                        </button>
                        <button
                          data-testid={`news-toggle-${n.id}`}
                          onClick={() => togglePublished(n)}
                          title={n.published ? "Despublicar" : "Publicar"}
                          className={`inline-flex items-center gap-1 px-2 py-1 border text-[10px] uppercase tracking-wide ${
                            n.published
                              ? "border-amber-300 text-amber-700 hover:bg-amber-50"
                              : "border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          }`}
                        >
                          {n.published ? (
                            <>
                              <EyeOff size={12} /> Despublicar
                            </>
                          ) : (
                            <>
                              <Eye size={12} /> Publicar
                            </>
                          )}
                        </button>
                        <button
                          data-testid={`news-delete-${n.id}`}
                          onClick={() => remove(n)}
                          title="Excluir"
                          className="px-2 py-1 border border-rose-300 text-rose-700 text-[10px] uppercase tracking-wide hover:bg-rose-50"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {editing && editForm && (
        <div className="fixed inset-0 bg-black/60 z-50 grid place-items-center p-4 overflow-auto">
          <div className="bg-white rounded-md w-full max-w-2xl p-6 my-8">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500">
                  Editar publicação
                </div>
                <h3 className="mt-1 font-serif-display text-2xl">
                  {editing.title}
                </h3>
              </div>
              <button
                onClick={() => {
                  setEditing(null);
                  setEditForm(null);
                  setEditCover(null);
                }}
                className="text-slate-500 hover:text-slate-900 text-2xl leading-none"
                aria-label="Fechar"
              >
                ×
              </button>
            </div>
            <NewsForm
              form={editForm}
              setForm={setEditForm}
              file={editCover}
              setFile={setEditCover}
              onSubmit={submitEdit}
              onCancel={() => {
                setEditing(null);
                setEditForm(null);
                setEditCover(null);
              }}
              busy={busy}
              submitLabel="Salvar alterações"
              existingCoverUrl={
                editing.cover_name
                  ? `${BACKEND}/api/public/news/${editing.id}/cover`
                  : null
              }
            />
          </div>
        </div>
      )}
    </div>
  );
}

const NewsForm = ({
  form,
  setForm,
  file,
  setFile,
  onSubmit,
  onCancel,
  busy,
  submitLabel,
  existingCoverUrl,
}) => (
  <form
    data-testid="news-form"
    onSubmit={onSubmit}
    className="grid md:grid-cols-2 gap-4"
  >
    <div className="md:col-span-2">
      <label className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
        Título *
      </label>
      <input
        data-testid="news-title"
        required
        minLength={3}
        value={form.title}
        onChange={(e) => setForm({ ...form, title: e.target.value })}
        className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm"
      />
    </div>
    <div>
      <label className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
        Categoria *
      </label>
      <select
        data-testid="news-category"
        value={form.category}
        onChange={(e) => setForm({ ...form, category: e.target.value })}
        className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm bg-white"
      >
        {CATEGORIES.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
    </div>
    <div>
      <label className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
        Autor / Fonte
      </label>
      <input
        data-testid="news-author"
        value={form.author}
        onChange={(e) => setForm({ ...form, author: e.target.value })}
        className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm"
        placeholder="Ex.: ABLEMP / Diretoria"
      />
    </div>
    <div className="md:col-span-2">
      <label className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
        Resumo (aparece nos cards)
      </label>
      <textarea
        data-testid="news-summary"
        value={form.summary}
        onChange={(e) => setForm({ ...form, summary: e.target.value })}
        className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm min-h-[90px]"
      />
    </div>
    <div className="md:col-span-2">
      <label className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
        Conteúdo completo
      </label>
      <textarea
        data-testid="news-content"
        value={form.content}
        onChange={(e) => setForm({ ...form, content: e.target.value })}
        className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm min-h-[220px]"
        placeholder="Texto completo da publicação..."
      />
    </div>
    <div className="md:col-span-2">
      <label className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
        URL da fonte (opcional)
      </label>
      <input
        data-testid="news-source-url"
        type="url"
        value={form.source_url}
        onChange={(e) => setForm({ ...form, source_url: e.target.value })}
        className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm"
        placeholder="https://..."
      />
    </div>
    <div className="md:col-span-2">
      <label className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
        Capa (imagem JPG/PNG até 10MB)
      </label>
      <input
        data-testid="news-cover"
        type="file"
        accept="image/*"
        onChange={(e) => setFile(e.target.files?.[0] || null)}
        className="mt-1 w-full text-sm"
      />
      {existingCoverUrl && !file && (
        <div className="mt-2 flex items-center gap-3">
          <img
            src={existingCoverUrl}
            alt="Capa atual"
            className="h-16 w-24 object-cover border border-slate-200"
          />
          <span className="text-xs text-slate-500">
            Capa atual — selecione um novo arquivo para substituir.
          </span>
        </div>
      )}
    </div>
    <div className="md:col-span-2 flex items-center gap-2">
      <input
        id="news-published"
        data-testid="news-published"
        type="checkbox"
        checked={form.published}
        onChange={(e) => setForm({ ...form, published: e.target.checked })}
        className="h-4 w-4"
      />
      <label htmlFor="news-published" className="text-sm text-slate-700">
        Publicar imediatamente (deixe desmarcado para salvar como rascunho)
      </label>
    </div>
    <div className="md:col-span-2 flex gap-2">
      <button
        disabled={busy}
        data-testid="news-submit"
        className="px-5 py-2.5 bg-[#0A0F14] text-white text-[11px] uppercase tracking-[0.22em] hover:bg-[#002776] disabled:opacity-60"
      >
        {busy ? "Salvando..." : submitLabel}
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="px-5 py-2.5 border border-slate-300 text-[11px] uppercase tracking-[0.22em] hover:border-[#002776]"
      >
        Cancelar
      </button>
    </div>
  </form>
);
